import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Clock, AlertTriangle, Sparkles, Mail, Phone, MapPin, Truck } from 'lucide-react';
import { useCart } from '../components/CartContext';
import { fbPurchase } from '../lib/metaPixel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 2000;

const formatAddress = (addr) => {
  if (!addr) return null;
  const lines = [
    addr.line1,
    addr.line2,
    [addr.postal_code, addr.city].filter(Boolean).join(' '),
    [addr.state, addr.country].filter(Boolean).join(', '),
  ].filter(Boolean);
  return lines;
};

const CheckoutSuccess = () => {
  const { search } = useLocation();
  const sessionId = new URLSearchParams(search).get('session_id');
  const { clear } = useCart();
  const [state, setState] = useState({ phase: 'checking', data: null, error: '' });

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: 'error', data: null, error: 'Missing session.' });
      return;
    }
    let cancelled = false;

    const poll = async (attempt = 0) => {
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        const data = res.data;
        if (cancelled) return;

        if (data.payment_status === 'paid') {
          setState({ phase: 'success', data, error: '' });
          // Meta Pixel: Purchase event — fires once when payment confirmed
          if (data.amount_total) {
            fbPurchase({
              id: data.bundle_id,
              value: Number((data.amount_total / 100).toFixed(2)),
              currency: String(data.currency || 'USD').toUpperCase(),
              quantity: data.qty || 1,
            });
          }
          clear();
          return;
        }
        if (data.status === 'expired') {
          setState({ phase: 'expired', data, error: '' });
          return;
        }
        if (attempt + 1 >= MAX_ATTEMPTS) {
          setState({ phase: 'pending', data, error: '' });
          return;
        }
        setTimeout(() => poll(attempt + 1), POLL_INTERVAL_MS);
      } catch (err) {
        if (cancelled) return;
        setState({
          phase: 'error',
          data: null,
          error: err?.response?.data?.detail || 'Could not verify payment status.',
        });
      }
    };

    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [sessionId]);

  const ui = (() => {
    switch (state.phase) {
      case 'success':
        return { icon: CheckCircle2, color: '#2D5A3D', title: 'Payment confirmed', body: "Thank you! Your order is on its way. We've sent a confirmation to your email." };
      case 'pending':
        return { icon: Clock, color: '#9C7400', title: 'Almost there', body: "Your payment is still being processed. You'll receive an email confirmation shortly." };
      case 'expired':
        return { icon: AlertTriangle, color: '#B23A48', title: 'Session expired', body: 'This checkout session expired. Please try again from the product page.' };
      case 'error':
        return { icon: AlertTriangle, color: '#B23A48', title: 'Something went wrong', body: state.error };
      default:
        return { icon: Sparkles, color: '#2D5A3D', title: 'Verifying your payment', body: 'Hold tight — confirming with Stripe...' };
    }
  })();

  const Icon = ui.icon;
  const d = state.data || {};
  const shippingLines = formatAddress(d.shipping_address);
  const billingLines = formatAddress(d.billing_address);

  return (
    <main style={{ backgroundColor: '#FAFAF7', minHeight: '70vh' }}>
      <div className="max-w-[720px] mx-auto px-6 md:px-12 py-16 md:py-24 text-center">
        <div
          className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-6"
          style={{ backgroundColor: '#F5EDE0' }}
        >
          <Icon size={32} color={ui.color} />
        </div>
        <h1 className="font-serif text-[36px] md:text-[44px] leading-[1.1]" style={{ color: '#2C2418' }}>
          {ui.title}
        </h1>
        <p className="mt-4 text-[15px] leading-relaxed" style={{ color: '#2C2418' }}>
          {ui.body}
        </p>

        {state.phase === 'success' && d.amount_total > 0 && (
          <div className="mt-8 inline-flex items-center gap-2 px-5 py-3 rounded-full" style={{ backgroundColor: '#F5EDE0', color: '#2C2418' }}>
            <span className="text-[12px] uppercase tracking-[0.18em] opacity-75">Total paid</span>
            <span className="text-[15px] font-bold">
              ${(d.amount_total / 100).toFixed(2)} {String(d.currency || '').toUpperCase()}
            </span>
          </div>
        )}

        {/* Captured customer & shipping data */}
        {state.phase === 'success' && (
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
            <div className="rounded-2xl p-5" style={{ backgroundColor: '#FFFFFF', border: '1px solid rgba(45,90,61,0.15)' }}>
              <p className="text-[12px] uppercase tracking-[0.18em] mb-3" style={{ color: '#2D5A3D' }}>
                Contact
              </p>
              {d.customer_name && (
                <p className="text-[14px] font-semibold mb-2" style={{ color: '#2C2418' }}>
                  {d.customer_name}
                </p>
              )}
              {d.customer_email && (
                <p className="flex items-center gap-2 text-[13px] mb-1.5" style={{ color: '#2C2418' }}>
                  <Mail size={14} color="#2D5A3D" /> {d.customer_email}
                </p>
              )}
              {d.customer_phone && (
                <p className="flex items-center gap-2 text-[13px]" style={{ color: '#2C2418' }}>
                  <Phone size={14} color="#2D5A3D" /> {d.customer_phone}
                </p>
              )}
            </div>

            <div className="rounded-2xl p-5" style={{ backgroundColor: '#FFFFFF', border: '1px solid rgba(45,90,61,0.15)' }}>
              <p className="text-[12px] uppercase tracking-[0.18em] mb-3 flex items-center gap-2" style={{ color: '#2D5A3D' }}>
                <Truck size={14} /> Shipping address
              </p>
              {shippingLines && shippingLines.length > 0 ? (
                <div className="text-[13px] leading-[1.55]" style={{ color: '#2C2418' }}>
                  {shippingLines.map((line, i) => (
                    <p key={i}>{line}</p>
                  ))}
                </div>
              ) : (
                <p className="text-[13px] italic opacity-70" style={{ color: '#2C2418' }}>
                  No shipping address captured.
                </p>
              )}
            </div>

            {billingLines && billingLines.length > 0 && (
              <div className="rounded-2xl p-5 md:col-span-2" style={{ backgroundColor: '#FFFFFF', border: '1px solid rgba(45,90,61,0.15)' }}>
                <p className="text-[12px] uppercase tracking-[0.18em] mb-3 flex items-center gap-2" style={{ color: '#2D5A3D' }}>
                  <MapPin size={14} /> Billing address
                </p>
                <div className="text-[13px] leading-[1.55]" style={{ color: '#2C2418' }}>
                  {billingLines.map((line, i) => (
                    <p key={i}>{line}</p>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-10 flex items-center justify-center gap-3 flex-wrap">
          <Link
            to="/"
            className="px-7 py-3 rounded-full text-[13px] uppercase tracking-[0.18em] font-semibold btn-hover"
            style={{ backgroundColor: '#2D5A3D', color: '#FFFFFF' }}
          >
            Back to home
          </Link>
          <Link
            to="/products/korean-scalp-revival-bar"
            className="px-7 py-3 rounded-full text-[13px] uppercase tracking-[0.18em] font-semibold border-2 btn-hover hover:bg-[#2D5A3D] hover:text-white"
            style={{ borderColor: '#2D5A3D', color: '#2D5A3D' }}
          >
            Continue shopping
          </Link>
        </div>

        {sessionId && (
          <p className="mt-8 text-[11px] opacity-50 break-all" style={{ color: '#2C2418' }}>
            Ref: {sessionId}
          </p>
        )}
      </div>
    </main>
  );
};

export default CheckoutSuccess;
